import { useEffect, useState } from 'react'
import { Paperclip, FileText, FileCode2 } from 'lucide-react'
import api from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/components/ui/toast'
import { formatBRL, formatDate } from '@/lib/utils'

function assuntoPadrao(nf) {
  return `NFS-e ${nf?.numero || ''} - PRECISA Tecnologia`.replace('  ', ' ').trim()
}
function descricaoPadrao(nf) {
  return [
    `Prezado(a) ${nf?.clienteNome || 'cliente'},`,
    '',
    `Segue em anexo a Nota Fiscal de Serviço Eletrônica (NFS-e) nº ${nf?.numero || ''}` +
      `, no valor de ${formatBRL(nf?.valor)}, referente aos serviços prestados pela PRECISA Tecnologia.`,
    '',
    'Anexos: arquivo XML e PDF (DANFSe) da nota fiscal.',
    '',
    'Atenciosamente,',
    'PRECISA Tecnologia',
  ].join('\n')
}

export default function EmailModal({ nf, onClose, onSent }) {
  const toast = useToast()
  const open = !!nf
  const [detalhe, setDetalhe] = useState(null)
  const [destinatario, setDestinatario] = useState('')
  const [assunto, setAssunto] = useState('')
  const [descricao, setDescricao] = useState('')
  const [enviando, setEnviando] = useState(false)

  useEffect(() => {
    if (!nf) return
    // Destinatário padrão: e-mail cadastrado no cliente (coluna `cliente.email`).
    setDestinatario(nf.clienteEmail || '')
    setAssunto(assuntoPadrao(nf))
    setDescricao(descricaoPadrao(nf))
    setDetalhe(null)
    api.get(`/nfs/${nf.nfID}`).then(({ data }) => {
      setDetalhe(data)
      // Fallback caso a linha da listagem não trouxesse o e-mail; nunca sobrescreve o que o usuário digitou.
      setDestinatario((atual) => atual || data.clienteEmail || '')
    }).catch(() => {})
  }, [nf])

  async function enviar() {
    setEnviando(true)
    try {
      await api.post(`/nfs/${nf.nfID}/email`, { destinatario, assunto, descricao })
      toast.success('E-mail enviado com sucesso.')
      onSent?.()
      onClose()
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Falha ao enviar o e-mail.')
    } finally {
      setEnviando(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Enviar NF por e-mail"
      description={nf ? `NFS-e nº ${nf.numero || ''} · ${nf.clienteNome}` : undefined}
      size="lg"
      footer={<>
        <Button variant="outline" onClick={onClose}>Fechar</Button>
        <Button onClick={enviar} disabled={enviando || !destinatario}>{enviando ? 'Enviando...' : 'Enviar e-mail'}</Button>
      </>}
    >
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label>Destinatário</Label>
          <Input type="email" value={destinatario} onChange={(e) => setDestinatario(e.target.value)} placeholder="cliente@exemplo.com" autoFocus />
        </div>
        <div className="space-y-1.5">
          <Label>Assunto</Label>
          <Input value={assunto} onChange={(e) => setAssunto(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label>Mensagem</Label>
          <textarea
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            rows={7}
            className="flex w-full rounded-md border border-border bg-input px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>

        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <Paperclip className="h-4 w-4" />
          <span className="inline-flex items-center gap-1"><FileCode2 className="h-4 w-4" /> XML</span>
          <span className="inline-flex items-center gap-1"><FileText className="h-4 w-4" /> PDF (DANFSe)</span>
        </div>

        {detalhe?.emails?.length > 0 && (
          <div>
            <Label className="mb-2 block">Envios anteriores</Label>
            <div className="space-y-1.5 max-h-40 overflow-auto">
              {detalhe.emails.map((em) => (
                <div key={em.nfEmailID} className="flex items-center gap-3 text-sm border-t border-border/60 pt-1.5">
                  <Badge variant={em.status === 'enviado' ? 'success' : 'destructive'}>{em.status}</Badge>
                  <span>{em.destinatario}</span>
                  <span className="text-muted-foreground text-xs ml-auto">{formatDate(em.criadoEm)} · {em.usuarioNome || '—'}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Dialog>
  )
}
